/**
 * Skaper-samarbeid – opprett/rediger promo-pakke (egen side, ikke modal).
 */
import React, { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import Layout from "../components/Layout";
import api from "../lib/api";
import { AuthImage } from "../components/collab/AuthMedia";

const PLATFORMS = ["onlyfans", "fansly", "snapchat", "instagram", "x", "tiktok", "reddit", "bluesky", "other"];

export default function SamarbeidPakke() {
  const { user, loading } = useAuth();
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id || id === "ny";

  const [loaded, setLoaded] = useState(isNew);
  const [packet, setPacket] = useState(null);
  const [title, setTitle] = useState("");
  const [variants, setVariants] = useState([""]);
  const [platforms, setPlatforms] = useState([]);
  const [accountIds, setAccountIds] = useState([]);
  const [validFrom, setValidFrom] = useState("");
  const [validTo, setValidTo] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [mediaUrls, setMediaUrls] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/me/collab-accounts").then((r) => setAccounts(r.data?.results || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (isNew) return;
    api.get(`/collab/promo-packets`).then((r) => {
      const list = r.data?.results || [];
      const p = list.find((x) => x.id === id);
      if (!p) {
        toast.error(t("collab.save_err"));
        navigate("/skaper/samarbeid?tab=packets");
        return;
      }
      setPacket(p);
      setTitle(p.title || "");
      setVariants(p.text_variants && p.text_variants.length > 0 ? p.text_variants : [""]);
      setPlatforms(p.platforms || []);
      setAccountIds(p.account_ids || []);
      setValidFrom(p.valid_from || "");
      setValidTo(p.valid_to || "");
      setMediaUrls(p.media_urls || []);
      setLoaded(true);
    }).catch(() => {
      toast.error(t("collab.save_err"));
      navigate("/skaper/samarbeid?tab=packets");
    });
  }, [id, isNew, navigate, t]);

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (!user.is_creator && !["admin", "owner"].includes(user.role)) {
    return <Navigate to="/skaper/samarbeid" replace />;
  }
  if (!loaded) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto px-4 py-12 text-center text-[var(--cream-soft)] font-fraunces italic">
          {t("common.loading")}…
        </div>
      </Layout>
    );
  }

  const accountsByPlatform = accounts.reduce((acc, a) => {
    (acc[a.platform] = acc[a.platform] || []).push(a);
    return acc;
  }, {});

  const togglePlatform = (p) => setPlatforms(platforms.includes(p) ? platforms.filter((x) => x !== p) : [...platforms, p]);
  const toggleAccount = (accId) => setAccountIds((cur) => cur.includes(accId) ? cur.filter((x) => x !== accId) : [...cur, accId]);

  const save = async () => {
    if (!title.trim()) { toast.error(t("collab.err_title_required")); return; }
    const cleanVariants = variants.map(v => v.trim()).filter(Boolean);
    if (cleanVariants.length === 0 && mediaUrls.length === 0 && pendingFiles.length === 0) {
      toast.error(t("collab.err_text_or_media"));
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        text_variants: cleanVariants,
        media_urls: mediaUrls,
        platforms,
        account_ids: accountIds,
        valid_from: validFrom || null,
        valid_to: validTo || null,
      };
      let savedId = id;
      if (isNew) {
        const r = await api.post("/collab/promo-packets", payload);
        savedId = r.data?.id;
      } else {
        await api.patch(`/collab/promo-packets/${id}`, payload);
      }
      if (pendingFiles.length > 0 && savedId) {
        for (const f of pendingFiles) {
          const fd = new FormData();
          fd.append("file", f);
          try {
            await api.post(`/collab/promo-packets/${savedId}/media`, fd, {
              headers: { "Content-Type": "multipart/form-data" },
            });
          } catch (uerr) {
            toast.error(uerr?.response?.data?.detail || t("collab.save_err"));
          }
        }
      }
      toast.success(t("collab.packet_saved"));
      navigate("/skaper/samarbeid?tab=packets");
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    } finally {
      setSaving(false);
    }
  };

  const uploadMedia = async (file) => {
    if (file.size > 30 * 1024 * 1024) {
      toast.error(t("collab.err_file_too_big"));
      return;
    }
    if (isNew) {
      setPendingFiles([...pendingFiles, file]);
      toast.success(t("collab.media_queued"));
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/collab/promo-packets/${id}/media`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setMediaUrls([...mediaUrls, r.data.url]);
      toast.success(t("collab.media_uploaded"));
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("collab.save_err"));
    } finally {
      setUploading(false);
    }
  };

  return (
    <Layout>
      <div className="max-w-2xl mx-auto px-4 py-8" data-testid="packet-page">
        <div className="mb-6">
          <Link to="/skaper/samarbeid?tab=packets" className="text-[var(--cream-faint)] hover:text-[var(--magenta)] inline-flex items-center gap-1.5"
            data-testid="packet-back">
            <ArrowLeft className="h-4 w-4" /> {t("collab.back_to_collab", "Tilbake til samarbeid")}
          </Link>
        </div>

        <h1 className="font-fraunces italic text-3xl text-[var(--cream)] mb-6">
          {isNew ? t("collab.new_packet") : t("collab.edit_packet")}
        </h1>

        <div className="space-y-5">
          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.packet_title_label")}
            </label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120}
              placeholder={t("collab.packet_title_placeholder")}
              className="input-editorial w-full" data-testid="packet-title" />
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.packet_accounts_label")}
            </label>
            {accounts.length === 0 ? (
              <p className="text-xs text-[var(--cream-faint)]">{t("collab.no_accounts_hint")}</p>
            ) : (
              <div className="space-y-2">
                {Object.entries(accountsByPlatform).map(([plat, list]) => (
                  <div key={plat}>
                    <div className="font-meta text-[9px] tracking-[0.15em] text-[var(--cream-faint)] mb-1">{plat.toUpperCase()}</div>
                    <div className="flex flex-wrap gap-1">
                      {list.map((a) => (
                        <button key={a.id} type="button" onClick={() => toggleAccount(a.id)}
                          data-testid={`packet-account-${a.id}`}
                          className={`px-2 py-1 font-meta text-[11px] border ${accountIds.includes(a.id) ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}>
                          @{a.handle}{a.label ? ` ·${a.label}` : ""}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-[var(--cream-faint)] mt-1.5">{t("collab.packet_accounts_hint")}</p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
                {t("collab.valid_from")}
              </label>
              <input type="date" value={validFrom} onChange={(e) => setValidFrom(e.target.value)}
                className="input-editorial w-full" data-testid="packet-valid-from" />
            </div>
            <div>
              <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
                {t("collab.valid_to")}
              </label>
              <input type="date" value={validTo} onChange={(e) => setValidTo(e.target.value)}
                className="input-editorial w-full" data-testid="packet-valid-to" />
            </div>
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.other_platforms_no_account")}
            </label>
            <div className="flex flex-wrap gap-1">
              {PLATFORMS.filter((p) => !accountsByPlatform[p]).map((p) => (
                <button key={p} type="button" onClick={() => togglePlatform(p)}
                  data-testid={`packet-platform-${p}`}
                  className={`px-3 py-1.5 font-meta text-[11px] border ${platforms.includes(p) ? "border-[var(--magenta)] text-[var(--magenta)] bg-[var(--magenta)]/10" : "border-white/15 text-[var(--cream-soft)]"}`}>
                  {p.toUpperCase()}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.text_label")}
            </label>
            <textarea value={variants[0] || ""} onChange={(e) => setVariants([e.target.value])} rows={5} maxLength={2000}
              placeholder={t("collab.text_placeholder")}
              className="input-editorial w-full font-manrope text-sm"
              data-testid="packet-text" />
          </div>

          <div>
            <label className="font-meta text-[10px] tracking-[0.2em] text-[var(--cream-faint)] block mb-1.5">
              {t("collab.media_label")}
            </label>
            {(mediaUrls.length > 0 || pendingFiles.length > 0) && (
              <div className="grid grid-cols-3 gap-2 mb-3">
                {mediaUrls.map((url, i) => (
                  <div key={`u-${i}`} className="relative aspect-square bg-black/40 border border-white/10">
                    <AuthImage url={url} alt="" className="w-full h-full object-cover" />
                    <button onClick={() => setMediaUrls(mediaUrls.filter((_, j) => i !== j))}
                      className="absolute top-1 right-1 p-1 bg-black/70 text-red-300">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                {pendingFiles.map((f, i) => (
                  <div key={`p-${i}`} className="relative aspect-square bg-black/40 border border-dashed border-[var(--magenta)]/40 flex flex-col items-center justify-center p-2">
                    <span className="font-meta text-[10px] text-[var(--magenta)] mb-1">{t("collab.pending")}</span>
                    <span className="text-xs text-[var(--cream-soft)] text-center break-all line-clamp-2">{f.name}</span>
                    <button onClick={() => setPendingFiles(pendingFiles.filter((_, j) => i !== j))}
                      className="absolute top-1 right-1 p-1 bg-black/70 text-red-300">
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <label className="inline-flex items-center gap-2 px-3 py-2 border border-white/15 text-sm text-[var(--cream-soft)] cursor-pointer hover:border-white/30">
              <Upload className="h-4 w-4" />
              {uploading ? t("collab.uploading") : t("collab.upload_media")}
              <input type="file" accept="image/*,video/*" className="hidden"
                disabled={uploading}
                onChange={(e) => { if (e.target.files?.[0]) uploadMedia(e.target.files[0]); e.target.value = ""; }}
                data-testid="packet-upload" />
            </label>
            {isNew && (
              <p className="text-xs text-[var(--cream-faint)] mt-1">{t("collab.media_will_upload_on_save")}</p>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Link to="/skaper/samarbeid?tab=packets" className="btn-ghost flex-1 text-center" data-testid="packet-cancel">
              {t("common.cancel")}
            </Link>
            <button onClick={save} disabled={saving} className="btn-magenta flex-1 disabled:opacity-40"
              data-testid="packet-save">
              {saving ? t("collab.saving") : t("common.save")}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
